import { API_PATHS } from '@/constants/api-paths'
import { api } from '@/lib/api'
import type { ApiResponse } from '@/types'

export type AiAssistIntent =
  | 'job'
  | 'booking'
  | 'query'
  | 'job_update'
  | 'job_assignment_update'
  | 'unknown'

export type AiAssistIntentHint = 'auto' | 'job' | 'booking' | 'query'

export type AiAssistJobDraft = {
  title?: string
  description?: string
  category?: number | string
  client?: string
  customer_name?: string
  email?: string
  phone?: string
  status?: string
  priority?: string
  scheduled_start?: string
  scheduled_end?: string
  scheduled_hours?: number | string
  job_pay?: number | string
  first_line?: string
  city?: string
  job_landmark?: string
  answer_text?: string
  data?: Record<string, unknown>
  [key: string]: unknown
}

export type AiAssistParseResult = {
  intent: AiAssistIntent
  draft: AiAssistJobDraft
  missing_fields: string[]
  field_errors: Record<string, string[]>
  ready: boolean
  notes: string
  next_action?: {
    method?: string
    path?: string
    via?: string
  } | null
}

export type AiAssistLocationState = {
  aiAssistDraft?: AiAssistParseResult
}

type ParseCreateOptions = {
  statement: string
  intentHint?: AiAssistIntentHint
  serviceInstanceId?: string | null
}

function unwrapData<T>(payload: unknown): T {
  if (payload && typeof payload === 'object' && 'data' in payload) {
    return (payload as ApiResponse<T>).data
  }
  return payload as T
}

function normalizeIntent(value: unknown): AiAssistIntent {
  const intent = String(value || '').trim().toLowerCase()
  if (
    intent === 'job'
    || intent === 'booking'
    || intent === 'query'
    || intent === 'job_update'
    || intent === 'job_assignment_update'
  ) {
    return intent
  }
  return 'unknown'
}

export const aiAssistApi = {
  async parseCreate(options: ParseCreateOptions): Promise<AiAssistParseResult> {
    const statement = String(options.statement || '').trim()
    if (!statement) {
      throw new Error('Statement is required.')
    }

    const response = await api.post<ApiResponse<AiAssistParseResult>>(
      API_PATHS.aiAssist.parseCreate,
      {
        statement,
        intent_hint: options.intentHint ?? 'auto',
      },
      {
        params: options.serviceInstanceId
          ? { service_instance_id: options.serviceInstanceId }
          : undefined,
      },
    )

    const result = unwrapData<AiAssistParseResult>(response.data)
    return {
      intent: normalizeIntent(result?.intent),
      draft: (result?.draft && typeof result.draft === 'object' ? result.draft : {}) as AiAssistJobDraft,
      missing_fields: Array.isArray(result?.missing_fields) ? result.missing_fields.map(String) : [],
      ready: Boolean(result?.ready),
      notes: String(result?.notes || '').trim(),
      field_errors: result?.field_errors && typeof result.field_errors === 'object'
        ? result.field_errors
        : {},
      next_action: result?.next_action ?? null,
    }
  },
}

export default aiAssistApi
